import {
  ButtonDS,
  InputDS,
  isPin4Digits,
  ModalDS,
  Spacer,
  TextCapitalized,
  validateUUID,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { unblockPinOfflineAsync } from "../cardsSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectAdminUser } from "../../homeSlice";

export const UnblockPinOfflineModal = (props: {
  cardsUuidAccepted: string[];
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { visible, onCloseModal } = props;
  const [cardUuid, setCardUuid] = useState<string>("");
  const [pin, setPin] = useState<string>("");
  const [displayError, setDisplayError] = useState<boolean>(false);

  const isValidUuid = useMemo(
    () => validateUUID(cardUuid) && props.cardsUuidAccepted.includes(cardUuid),
    [cardUuid, props.cardsUuidAccepted],
  );
  const isValidPin = useMemo(() => isPin4Digits(pin), [pin]);
  const adminUser = useAppSelector(selectAdminUser);

  const reset = useCallback(() => {
    setDisplayError(false);
    setPin("");
    setCardUuid("");
    onCloseModal();
  }, [onCloseModal]);

  const onSubmit = useCallback(() => {
    setDisplayError(true);
    if (!isValidUuid || !isValidPin || !adminUser) return;
    void dispatch(
      unblockPinOfflineAsync({
        cardUuid: cardUuid.trim(),
        pin: pin.trim(),
        updatedBy: adminUser.email,
      }),
    );
    reset();
  }, [dispatch, cardUuid, isValidPin, isValidUuid, pin, reset, adminUser]);

  return (
    <ModalDS visible={visible} onClose={() => reset()} width={40}>
      <StyledColumn>
        <StyledTitle>
          <TextCapitalized>
            {t("cards_shipments.pin.tab.unblockPinOffline")}
          </TextCapitalized>
        </StyledTitle>
        <Spacer y={2} />
        <InputDS
          placeholder={t("cards_shipments.pin.input.cardUuid")}
          value={cardUuid}
          error={
            displayError && !validateUUID(cardUuid)
              ? t("cards_shipments.pin.input.cardUuid.error")
              : displayError && !props.cardsUuidAccepted.includes(cardUuid)
                ? t("cards_shipments.pin.input.cardUuid.notAccepted")
                : undefined
          }
          onChange={(e) => setCardUuid(e.target.value)}
        />
        <InputDS
          placeholder={t("cards_shipments.pin.input.pin")}
          value={pin}
          error={
            displayError && !isValidPin
              ? t("cards_shipments.pin.input.pin.error")
              : undefined
          }
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void onSubmit()}
        />
        <Spacer y={1} />
        <ButtonDS
          text={t("cards_shipments.pin.button.submit")}
          format="fill"
          buttonType="primary"
          sizeButton="L"
          disabled={!isValidUuid || !isValidPin}
          onClick={() => void onSubmit()}
        />
      </StyledColumn>
    </ModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
  color: ${colors["colors/text/black"]};
`;
